import {
  ArrowLeftRight,
  BadgeCheck,
  Blocks,
  BookOpen,
  Boxes,
  Building2,
  Calculator,
  ChartColumn,
  CircleCheck,
  CreditCard,
  FileText,
  Gavel,
  GitBranch,
  Globe,
  Landmark,
  Layers,
  LayoutDashboard,
  Mailbox,
  MapPin,
  Network,
  RefreshCcw,
  ScrollText,
  SendHorizontal,
  ShieldCheck,
  Receipt,
  SlidersHorizontal,
  Store,
  UserCog,
  Users,
  Wallet,
  Workflow,
  BookText,
  Percent,
  type LucideIcon,
} from 'lucide-react';
import type { Permission } from '@fpc/shared';

export interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  /**
   * Hidden unless the user holds one of these.
   *
   * Empty means unrestricted — the same convention organisation scope uses —
   * and is reserved for pages that hold no data of their own, such as the
   * handbook.
   */
  permissions: Permission[];
  /**
   * Internal to the finance operation, so hidden from a vendor's own login
   * even when it names no permission — the handbook describes how the team
   * works, which is not an outside party's business.
   */
  staffOnly?: boolean;
}

/** Whether a signed-in user may see a menu entry at all. */
export function navItemVisible(
  item: NavItem,
  canAny: (...permissions: Permission[]) => boolean,
): boolean {
  if (item.staffOnly && isVendorLogin(canAny)) return false;
  return item.permissions.length === 0 || canAny(...item.permissions);
}

/** A vendor's own login: the one role that holds the vendor portal. */
export function isVendorLogin(canAny: (...permissions: Permission[]) => boolean): boolean {
  return canAny('vendor_portal:read');
}

/**
 * Where a signed-in user lands: the first screen the menu offers them that
 * holds data. The dashboard for every finance role; for a vendor login, which
 * has no dashboard, its own invoices. Never a page that would only refuse.
 */
export function homePath(canAny: (...permissions: Permission[]) => boolean): string {
  for (const group of NAV_GROUPS) {
    for (const item of group.items) {
      if (item.permissions.length > 0 && canAny(...item.permissions)) return item.to;
    }
  }
  return '/account';
}

/** Keeps a post-login destination inside this app and preserves its query/hash. */
export function safeInternalPath(target: string | null | undefined, fallback = '/'): string {
  const hasControlCharacter = [...(target ?? '')].some((character) => {
    const code = character.charCodeAt(0);
    return code < 0x20 || code === 0x7f;
  });
  if (
    !target ||
    !target.startsWith('/') ||
    target.startsWith('//') ||
    target.includes('\\') ||
    hasControlCharacter
  ) {
    return fallback;
  }
  try {
    const url = new URL(target, 'https://app.invalid');
    if (url.origin !== 'https://app.invalid') return fallback;
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return fallback;
  }
}

/** Builds the relative login URL without losing the destination's query string. */
export function loginPathFor(target: string): string {
  return `/login?next=${encodeURIComponent(target)}`;
}

export interface NavGroup {
  title: string | null;
  items: NavItem[];
}

/**
 * Administration is split the way the data is shaped, in the order it has to
 * be set up: Organisation, then Structure, then Masters, Access and Workflow.
 * Twelve flat links hid which screens are levels and which are lists.
 *
 * Navigation groups follow PRD §39 exactly: internal modules are not exposed
 * as menu items, and every entry is gated on the same permission the API
 * enforces, so the menu can never offer an action that would be refused.
 *
 * The sidebar and the command palette both read this list, so a page can only
 * ever be reachable one way by accident.
 */
export const NAV_GROUPS: NavGroup[] = [
  {
    title: null,
    items: [
      {
        to: '/dashboard',
        label: 'Dashboard',
        icon: LayoutDashboard,
        permissions: ['dashboard:read'],
      },
    ],
  },
  {
    // A vendor's own login. Nothing else in the menu is open to it.
    title: 'Vendor',
    items: [
      {
        to: '/vendor/invoices',
        label: 'My Invoices',
        icon: Receipt,
        permissions: ['vendor_portal:read'],
      },
    ],
  },
  {
    title: 'Operations',
    items: [
      { to: '/invoices', label: 'Invoices', icon: FileText, permissions: ['invoice:read'] },
      {
        // Operations rather than Administration: this is a queue people watch,
        // not a master an admin edits on somebody else's behalf.
        to: '/integrations/outlook',
        label: 'Invoice Mailbox',
        icon: Mailbox,
        permissions: ['mail_connection:manage', 'mail_connection:read_all'],
      },
      {
        to: '/approvals',
        label: 'Approvals',
        icon: CircleCheck,
        permissions: ['approval:read', 'approval:read_all'],
      },
      {
        to: '/accounting',
        label: 'Accounting',
        icon: Calculator,
        permissions: ['invoice:verify'],
      },
      {
        to: '/finance-requests',
        label: 'Treasury Requests',
        icon: Gavel,
        permissions: ['finance_request:read', 'finance_request:read_all'],
      },
      { to: '/payables', label: 'Payables', icon: Wallet, permissions: ['payable:read'] },
    ],
  },
  {
    title: 'Treasury',
    items: [
      {
        to: '/payments',
        label: 'Payment Queue',
        icon: SendHorizontal,
        permissions: ['obligation:read'],
      },
      {
        to: '/payments/batches',
        label: 'Payment Batches',
        icon: Layers,
        permissions: ['payment_batch:read'],
      },
      {
        to: '/banking/statements',
        label: 'Bank Statements',
        icon: Landmark,
        permissions: ['bank_statement:read'],
      },
      {
        to: '/banking/transactions',
        label: 'Bank Transactions',
        icon: ArrowLeftRight,
        permissions: ['bank_transaction:read'],
      },
      {
        to: '/reconciliation',
        label: 'Reconciliation',
        icon: RefreshCcw,
        permissions: ['reconciliation:read'],
      },
    ],
  },
  {
    title: 'Insights',
    items: [
      { to: '/reports', label: 'Reports', icon: ChartColumn, permissions: ['report:read'] },
      { to: '/audit', label: 'Audit Trail', icon: ScrollText, permissions: ['audit:read'] },
    ],
  },
  // Legal entities and the groups above them.
  {
    title: 'Organisation',
    items: [
      {
        to: '/settings/groups',
        label: 'Groups',
        icon: Boxes,
        permissions: ['group:read'],
      },
      {
        to: '/settings/companies',
        label: 'Companies',
        icon: Building2,
        permissions: ['company:read'],
      },
    ],
  },
  // The org axis (vertical → unit → department) and the geo axis (region → location). Each needs its company first.
  {
    title: 'Structure',
    items: [
      {
        to: '/settings/regions',
        label: 'Regions',
        icon: Globe,
        permissions: ['region:read'],
      },
      {
        to: '/settings/locations',
        label: 'Locations',
        icon: MapPin,
        permissions: ['location:read'],
      },
      {
        to: '/settings/verticals',
        label: 'Verticals',
        icon: Workflow,
        permissions: ['vertical:read'],
      },
      {
        to: '/settings/business-units',
        label: 'Business Units',
        icon: Blocks,
        permissions: ['business_unit:read'],
      },
      {
        to: '/settings/departments',
        label: 'Departments',
        icon: Network,
        permissions: ['department:read'],
      },
    ],
  },
  // Flat lists that belong to a company but to neither axis.
  {
    title: 'Masters',
    items: [
      { to: '/settings/vendors', label: 'Vendors', icon: Store, permissions: ['vendor:read'] },
      {
        to: '/settings/payment-accounts',
        label: 'Payment Accounts',
        icon: CreditCard,
        permissions: ['payment_account:read', 'loan:read'],
      },
      {
        to: '/settings/ledgers',
        label: 'General Ledgers',
        icon: BookText,
        permissions: ['ledger:read'],
      },
      {
        to: '/settings/tds-sections',
        label: 'TDS Sections',
        icon: Percent,
        permissions: ['tds_section:read'],
      },
    ],
  },
  // What a role permits, narrowed by where each user's scope applies.
  {
    // People: the employee master HR keeps, and the payroll paid to them.
    title: 'HR',
    items: [
      {
        to: '/hr/employees',
        label: 'Employees',
        icon: BadgeCheck,
        permissions: ['employee:read'],
      },
      {
        to: '/hr/projects',
        label: 'Projects',
        icon: Blocks,
        permissions: ['project:read'],
      },
      { to: '/payroll', label: 'Payroll', icon: Users, permissions: ['payroll:read'] },
    ],
  },
  {
    title: 'Access',
    items: [
      { to: '/settings/users', label: 'Users', icon: UserCog, permissions: ['user:read'] },
      { to: '/settings/roles', label: 'Roles', icon: ShieldCheck, permissions: ['role:read'] },
    ],
  },
  // Needs Structure's heads before it can route anything.
  {
    title: 'Workflow',
    items: [
      {
        to: '/settings/approvals',
        label: 'Approval Rules',
        icon: GitBranch,
        permissions: ['approval_rule:read'],
      },
    ],
  },
  // Per-company switches: features each company turns on or off for itself.
  {
    title: 'System',
    items: [
      {
        to: '/settings/system',
        label: 'System Settings',
        icon: SlidersHorizontal,
        permissions: ['company:read'],
      },
    ],
  },
  {
    title: 'Help',
    items: [
      // No permission: the handbook explains the refusals too, so it is for
      // everyone who can sign in.
      { to: '/cookbook', label: 'Cookbook', icon: BookOpen, permissions: [], staffOnly: true },
    ],
  },
];

/**
 * The bottom bar's tabs, in order of preference; the first four the user's role
 * can open are shown, so a tab the role cannot use gives its place to one it
 * can — a payroll user gets Payroll, Approvals and Reports rather than Invoices
 * and Payables. Each carries a short label that fits a quarter of a phone.
 */
const BOTTOM_NAV: Array<{ path: string; label: string }> = [
  { path: '/dashboard', label: 'Home' },
  { path: '/vendor/invoices', label: 'Invoices' },
  { path: '/invoices', label: 'Invoices' },
  { path: '/payables', label: 'Payables' },
  { path: '/payroll', label: 'Payroll' },
  { path: '/approvals', label: 'Approvals' },
  { path: '/payments', label: 'Payments' },
  { path: '/accounting', label: 'Accounting' },
  { path: '/finance-requests', label: 'Treasury' },
  { path: '/reconciliation', label: 'Reconcile' },
  { path: '/payments/batches', label: 'Batches' },
  { path: '/banking/statements', label: 'Statements' },
  { path: '/banking/transactions', label: 'Bank' },
  { path: '/reports', label: 'Reports' },
  { path: '/audit', label: 'Audit' },
  { path: '/hr/employees', label: 'Employees' },
];

/**
 * Roles whose day is one queue get it ahead of the general order: the accounts
 * team its verification queue and Treasury its payment queue, each in place of
 * Payables, which they can read but do not work from.
 */
const ROLE_TABS: Partial<Record<string, string[]>> = {
  ACCOUNTS_TEAM: ['/dashboard', '/invoices', '/accounting', '/approvals'],
  TREASURY: ['/dashboard', '/invoices', '/payments', '/approvals'],
};

/**
 * The first four tabs a user can open: the role's own tabs, if it has any, then
 * the preferred ones above, then — for a role that reaches fewer than four of
 * those, such as an administrator — the rest of its menu in menu order. Pages
 * that hold no data (the handbook) never take a tab.
 */
export function bottomNavItems(
  reachable: NavItem[],
  roleKey?: string,
): Array<NavItem & { short: string }> {
  const own = roleKey ? (ROLE_TABS[roleKey] ?? []) : [];
  const order = [
    ...own.flatMap((path) => BOTTOM_NAV.filter((tab) => tab.path === path)),
    ...BOTTOM_NAV.filter((tab) => !own.includes(tab.path)),
  ];
  const preferred = order.flatMap(({ path, label }) => {
    const item = reachable.find((entry) => entry.to === path);
    return item ? [{ ...item, short: label }] : [];
  });
  const rest = reachable
    .filter((item) => item.permissions.length > 0)
    .filter((item) => !preferred.some((entry) => entry.to === item.to))
    .map((item) => ({ ...item, short: item.label }));
  return [...preferred, ...rest].slice(0, 4);
}
